// ============================================
// Prenta — Custom Auth (Google OAuth)
// ============================================
// Replaces Auth0 with direct Google OAuth.
// Worker handles OAuth flow, frontend stores JWT.

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem('prenta_user');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const [token, setToken] = useState(() => localStorage.getItem('prenta_token'));
  const [isLoading, setIsLoading] = useState(true);

  // Check URL for auth callback params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    const urlUser = params.get('user');
    const authError = params.get('auth_error');

    if (authError) {
      console.error('Auth error:', authError);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      setIsLoading(false);
      return;
    }

    if (urlToken && urlUser) {
      try {
        const userData = JSON.parse(decodeURIComponent(urlUser));
        setToken(urlToken);
        setUser(userData);
        localStorage.setItem('prenta_token', urlToken);
        localStorage.setItem('prenta_user', JSON.stringify(userData));
      } catch (err) {
        console.error('Failed to parse auth callback:', err);
      }
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    setIsLoading(false);
  }, []);

  const login = useCallback(() => {
    window.location.href = `${API_URL}/api/auth/google`;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('prenta_token');
    localStorage.removeItem('prenta_user');
  }, []);

  const isAuthenticated = !!token && !!user;

  return React.createElement(AuthContext.Provider, {
    value: { user, token, isAuthenticated, isLoading, login, logout },
  }, children);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
